import { useState, type CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";
import { getTelegramUser, triggerHapticFeedback } from "../lib/tg";
import { AnimatedTabIcon, type AnimatedTabIconAsset } from "./AnimatedTabIcon";

export type BottomNavItem = {
  key: string;
  label: string;
  icon: LucideIcon;
  animatedIcon?: AnimatedTabIconAsset;
  active: boolean;
  onClick: () => void;
  avatar?: boolean;
};

export function BottomNav({ items }: { items: BottomNavItem[] }) {
  const [playKeys, setPlayKeys] = useState<Record<string, number>>({});
  const regularItems = items.filter((item) => !item.avatar);
  const avatarItem = items.find((item) => item.avatar);
  const activeRegularIndex = regularItems.findIndex((item) => item.active);

  function handleRegularClick(item: BottomNavItem) {
    triggerHapticFeedback();
    if (item.animatedIcon) {
      setPlayKeys((current) => ({
        ...current,
        [item.key]: (current[item.key] ?? 0) + 1,
      }));
    }
    item.onClick();
  }

  function handleAvatarClick(item: BottomNavItem) {
    triggerHapticFeedback();
    item.onClick();
  }

  return (
    <nav className="tp-bottom-nav" aria-label="основная навигация">
      <div
        className="tp-nav-main"
        data-count={regularItems.length}
        data-has-active={activeRegularIndex >= 0}
        data-active-index={Math.max(activeRegularIndex, 0)}
        style={
          {
            "--tp-nav-count": regularItems.length,
          } as CSSProperties
        }
      >
        <span className="tp-nav-active-indicator" aria-hidden="true" />
        {regularItems.map((item) => (
          <button
            key={item.key}
            className="tp-nav-item"
            data-active={item.active}
            onClick={() => handleRegularClick(item)}
            type="button"
          >
            {item.animatedIcon ? (
              <AnimatedTabIcon
                fallbackIcon={item.icon}
                gifSrc={item.animatedIcon.gifSrc}
                lottieData={item.animatedIcon.lottieData}
                durationMs={item.animatedIcon.durationMs}
                playKey={playKeys[item.key] ?? 0}
              />
            ) : (
              <item.icon className="tp-nav-icon" aria-hidden="true" />
            )}
            <span className="tp-nav-label">{item.label}</span>
          </button>
        ))}
      </div>

      {avatarItem && (
        <button
          className="tp-nav-avatar-island"
          data-active={avatarItem.active}
          onClick={() => handleAvatarClick(avatarItem)}
          type="button"
          aria-label={avatarItem.label}
        >
          <Avatar />
        </button>
      )}
    </nav>
  );
}

function Avatar() {
  const user = getTelegramUser();
  const name =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.username ||
    "t";
  const initials = name.trim().slice(0, 1).toUpperCase() || "T";

  if (user?.photo_url) {
    return (
      <img
        src={user.photo_url}
        alt=""
        className="tp-nav-avatar-image"
        draggable={false}
      />
    );
  }

  return <span className="tp-nav-avatar-fallback">{initials}</span>;
}
